import { isArchiveBattle, historyThumbnail } from '../../utils/historyArchive';
import useLiveRefresh from '../../hooks/useLiveRefresh';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';
import { Pagination } from 'antd';
import { useWebSocket } from '../../context/WebSocketContext';
import { message } from 'antd';
import { useUser } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';

// 导入 Cormorant 字体
const GlobalStyle = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant:wght@500&display=swap');
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: ${({ theme }) => theme.background};
  position: relative;
  overflow: hidden;
  padding-top: 0;
`;

const LogoWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: transparent;

  @media (max-width: 768px) {
    padding: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const LogoContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  position: relative;
  width: auto;
  padding: 24px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    padding: 24px;
  }
`;

const Logo = styled.img`
  width: 500px;
  height: 200px;
  object-fit: contain;
  display: block;
  opacity: 1;
  transition: opacity 0.3s ease;
  margin: 0;

  @media (max-width: 1440px) {
    width: 450px;
    height: 180px;
  }
  
  @media (max-width: 1024px) {
    width: 400px;
    height: 160px;
  }
  
  @media (max-width: 768px) {
    width: 360px;
    height: 120px;
    margin-left: 0;
  }
  
  @media (max-width: 480px) {
    width: 300px;
    height: 100px;
    margin-left: 0;
  }
  
  @media (max-width: 375px) {
    width: 240px;
    height: 80px;
    margin-left: 0;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  margin-top: ${({ $hasBattle }) => $hasBattle ? '300px' : '0'};
  padding: 0 24px;
  box-sizing: border-box;
  transition: margin 0.15s ease-out;
`;

const BattleSection = styled.section`
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 48px;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    padding-bottom: 24px;
    flex-direction: column;
    gap: 24px;
    align-items: flex-start;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const BattleHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 150px;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 0;
  padding-top: 0;
  margin-top: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    margin-top: 0;
    padding-top: 0;
    align-self: flex-start;
  }
`;

const BattleDate = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
  text-shadow: ${({ theme }) => theme.isDarkMode ? 'none' : '1px 1px 1px rgba(255, 255, 255, 0.8)'};
  padding-left: 0;
  line-height: 1.2;
  width: 100%;
  text-align: left;
  font-variant-numeric: tabular-nums;
  padding-top: 0;
  transform: translateY(-8px);
  margin-top: 24px;

  @media (max-width: 768px) {
    transform: translateY(0);
    margin-top: 0;
  }
`;

const BattleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  flex: 1;
  width: calc(100% - 150px - 48px - 8px);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    width: 100%;
  }
`;

const ViewButton = styled.button`
  color: ${({ theme }) => theme.text};
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  text-shadow: none;
  background: none;
  border: none;
  padding: 0;

  .arrow-icon {
    margin-left: 0;
    width: 24px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    overflow: visible;
    
    @media (max-width: 768px) {
      width: 20px;
      height: 16px;
    }

    @media (max-width: 480px) {
      width: 18px;
      height: 14px;
    }
    
    svg {
      width: 24px;
      height: 20px;
      position: relative;
      overflow: visible;
      
      @media (max-width: 768px) {
        width: 20px;
        height: 16px;
      }

      @media (max-width: 480px) {
        width: 18px;
        height: 14px;
      }
      
      path {
        transform-origin: left;
        transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
        stroke-width: 2;
      }
    }
  }
`;

const BattleCard = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  padding: 24px 24px 24px 24px;
  transition: all 0.12s ease-out;
  background: transparent;
  position: relative;
  height: auto;
  width: 100%;
  border-radius: 0;
  margin-left: 8px;
  
  @media (max-width: 1440px) {
    gap: 24px;
  }

  @media (max-width: 1024px) {
    gap: 24px;
  }
  
  @media (max-width: 768px) {
    gap: 24px;
    padding: 24px;
    margin-left: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)'};
    
    ${ViewButton} {
      color: #ff4d4f;
      
      .arrow-icon {
        svg {
          path {
            d: path("M0 10h40M40 10c-2 0-8-4-12-8M40 10c-2 0-8 4-12 8");
          }
        }
      }
    }
  }
`;

const ImageGroup = styled.div`
  flex: 1.2;
  display: flex;
  position: relative;
  z-index: 1;
  height: 240px;
  overflow: hidden;
  border-radius: 0;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  align-self: flex-start;

  @media (max-width: 768px) {
    height: 160px;
    width: 240px;
    flex: none;
  }
  
  @media (max-width: 480px) {
    height: 120px;
    width: 180px;
  }
  
  .main-image-container {
    flex: 2;
    position: relative;
    height: 100%;
    overflow: hidden;
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    @media (max-width: 768px) {
      border-radius: 0;
      flex: 1.5;
    }
    
    .main-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      will-change: transform;
      transform: translateZ(0);
      backface-visibility: hidden;
      -webkit-font-smoothing: subpixel-antialiased;
      image-rendering: -webkit-optimize-contrast;
    }

    .no-image-text {
      color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
      font-size: 14px;
      text-align: center;
      padding: 20px;
    }
  }
  
  .sub-images {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-left: 1px;
    
    @media (max-width: 768px) {
      flex: 1;
      display: flex;
    }
    
    .sub-image-container {
      width: 100%;
      height: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'};
      
      &:first-child {
        border-radius: 0;
        margin-bottom: 1px;
      }
      
      &:last-child {
        border-radius: 0;
      }
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        will-change: transform;
        transform: translateZ(0);
        backface-visibility: hidden;
        -webkit-font-smoothing: subpixel-antialiased;
        image-rendering: -webkit-optimize-contrast;
      }
      
      .no-image-text {
        color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
        font-size: 12px;
        text-align: center;
        padding: 10px;
      }
    }
  }

  ${BattleCard}:hover & {
    .main-image, .sub-images img {
      transform: none;
    }
  }
`;

const CardContent = styled.div`
  flex: 0.8;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  position: relative;
  z-index: 1;
  align-self: flex-start;

  @media (max-width: 768px) {
    padding: 0;
    flex: 1;
  }
`;

const BattleTitle = styled.h2`
  font-size: 24px;
  color: ${({ theme }) => theme.text};
  margin: 0;
  margin-bottom: 24px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
  margin-right: 4px;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;
  image-rendering: -webkit-optimize-contrast;
`;

const UserName = styled.span`
  font-size: 1rem;
  color: ${({ theme }) => theme.text};
`;

const WinnerLabel = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.secondaryText};
  margin: 0 0 1rem 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
  font-size: 24px;
  color: ${({ theme }) => theme.text};
  gap: 12px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  
  .ant-pagination {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .ant-pagination-item {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    margin: 0;
    padding: 0;
    background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.8)'};
    border: 1px solid #ff4d4f;
    border-radius: 4px;
    
    a {
      color: ${({ theme }) => theme.text};
      padding: 0 6px;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    &-active {
      background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 1)'};
      border-color: #ff4d4f;
      
      a {
        color: #ff4d4f;
      }
    }

    &:hover {
      border-color: #ff4d4f;
      a {
        color: #ff4d4f;
      }
    }
  }
  
  .ant-pagination-prev, .ant-pagination-next {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    margin: 0;
    
    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 0;
      background: none;
      border: none;
      color: ${({ theme }) => theme.text};
      
      &:hover {
        color: #ff4d4f;
      }
    }

    &:hover button {
      color: #ff4d4f;
    }
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
  font-size: 16px;
  color: ${({ theme }) => theme.secondaryText};
`;

const TopContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 32px 24px;
  position: relative;
  z-index: 1;
`;

const CardBattleInfo = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const BattleInfo = styled.div`
  width: 100%;
  background: var(--brand, #FF4B3F);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const BattleTheme = styled.div`
  font-weight: bold;
  font-size: 24px;
  margin-bottom: 8px;
`;

const BattleStatus = styled.div`
  font-size: 16px;
`;

const BattleTimer = styled.div`
  font-family: 'Cormorant', 'Times New Roman', serif;
  font-weight: 500;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
  font-feature-settings: 'tnum' on, 'lnum' on;
  color: ${({ $isPaused, theme, $isFixed }) =>
    $isPaused
      ? '#FF4B3F'
      : $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 36px;
    font-family: 'Cormorant', 'Times New Roman', serif !important;
    font-variant-numeric: lining-nums tabular-nums !important;
    font-feature-settings: 'tnum' on, 'lnum' on !important;
  }
`;

const PauseText = styled.span`
  font-size: 16px;
  color: #FF4B3F;
  line-height: 100%;
  margin-left: 10px;
  font-weight: normal;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-left: 8px;
  }
`;

const HistoryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--brand, #FF4B3F);
  color: white;
  border: none;
  border-radius: 200px;
  padding: 7px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 238px;
  height: 72px;
  transform: translateY(100%);

  &:hover {
    transform: translateY(calc(100% - 2px));
    box-shadow: 0 6px 16px rgba(200, 55, 43, 0.4);
    background: #e53e3e;
  }

  &:active {
    transform: translateY(100%);
    box-shadow: 0 2px 8px rgba(255, 75, 63, 0.3);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 1440px) {
    width: auto;
    height: auto;
  }

  @media (max-width: 768px) {
    transform: none;
    margin-top: 24px;
    width: auto;
    height: auto;
    
    &:hover {
      transform: translateY(-2px);
    }
    
    &:active {
      transform: none;
    }
  }
`;

const BattleBanner = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  width: 100%;
  height: auto;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.isDarkMode ? 'white' : '#000'};
  z-index: 8;
  opacity: 1;
  transition: all 0.15s ease-out;
  padding: 20px 0;
  backdrop-filter: none;
  box-shadow: none;
`;

const BannerContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.15s ease-out;
  
  @media (max-width: 1440px) {
    max-width: 100%;
  }
`;

const BannerTitle = styled.h1`
  font-family: HanaMinA;
  font-size: 64px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: 0%;
  width: 704px;
  height: 88px;
  margin: 0 0 10px 0;
  position: relative;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  transition: all 0.15s ease-out;
  
  @media (max-width: 768px) {
    font-size: 32px;
    width: 100%;
    height: auto;
  }
`;

const BannerThemeLabel = styled.span`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: block;
  margin-bottom: 5px;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  position: relative;
  z-index: 1;
  transition: all 0.15s ease-out;
`;

const BannerStatus = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  transition: all 0.15s ease-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const BannerPhase = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  transition: all 0.15s ease-out;
  
  span {
    font-size: 16px;
    font-weight: normal;
    opacity: 0.8;
    color: ${({ theme, $isFixed }) =>
      $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
    };
    text-shadow: ${({ theme, $isFixed }) =>
      $isFixed
        ? 'none'
        : theme.isDarkMode ? 'none' : 'none'
    };
  }
  
  @media (max-width: 768px) {
    span {
      font-size: 14px;
    }
  }
`;

const BannerTimer = styled.div`
  font-family: 'Cormorant', 'Times New Roman', serif;
  font-weight: 500;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
  font-feature-settings: 'tnum' on, 'lnum' on;
  color: ${({ $isPaused, theme, $isFixed }) =>
    $isPaused
      ? '#FF4B3F'
      : $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.15s ease-out;
  
  @media (max-width: 768px) {
    font-size: 36px;
    font-family: 'Cormorant', 'Times New Roman', serif !important;
    font-variant-numeric: lining-nums tabular-nums !important;
    font-feature-settings: 'tnum' on, 'lnum' on !important;
  }
`;

const BannerPausedLabel = styled.span`
  font-size: 16px;
  font-weight: normal;
  color: #FF4B3F;
  line-height: 100%;
  margin-left: 10px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-left: 8px;
  }
`;

const BannerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #ff4d4f;
  color: #ffffff;
  border: none;
  border-radius: 200px;
  padding: 7px 24px;
  width: 238px;
  height: 72px;
  font-family: 'HanaMinA';
  font-weight: 500;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s ease;
  white-space: nowrap;
  overflow: visible;
  position: relative;
  margin-top: 0;
  transform: none;
  
  svg {
    width: 64px;
    height: 64px;
    margin-left: 8px;
    
    path {
      transform-origin: left;
      transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
      stroke-width: 2;
    }

    @media (max-width: 768px) {
      width: 48px;
      height: 48px;
    }

    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
    }
  }
  
  &:hover {
    background: #f44d4f;
    
    svg path {
      d: path("M0 10h40M40 10c-2 0-8-4-12-8M40 10c-2 0-8 4-12 8");
    }
  }
  
  &:active {
    background: #e64545;
  }
  
  @media (max-width: 1440px) {
    font-size: 28px;
  }
  
  @media (max-width: 768px) {
    width: 200px;
    height: 60px;
    padding: 7px 20px;
    font-size: 24px;
  }
  
  @media (max-width: 375px) {
    width: 180px;
    height: 50px;
    padding: 5px 16px;
    font-size: 20px;
  }
`;

const BannerDescription = styled.div`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  position: relative;
  z-index: 1;
  
  .description-label {
    margin-bottom: 4px;
    font-weight: normal;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0%;
    color: ${({ theme, $isFixed }) =>
      $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
    };
  }
  
  .description-content-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const BannerAppendix = styled.a`
  color: var(--brand, #FF4B3F);
  text-decoration: none;
  font-size: 16px;
  font-weight: normal;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
  
  .download-icon {
    display: inline-flex;
    align-items: center;
  }
  
  .preview-img {
    width: 20px;
    height: 20px;
    object-fit: cover;
    border-radius: 2px;
    margin-right: 4px;
  }
`;

const BattleContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 0.3rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1440px) {
    max-width: 100%;
  }
  
  @media (max-width: 1024px) {
    padding: 0 0.25rem;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 0.2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.15rem;
  }
`;

const ThemeLabel = styled.span`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: block;
  margin-bottom: 5px;
  color: ${({ theme }) => theme.isDarkMode ? 'white' : '#000'};
  position: relative;
  z-index: 1;
`;

const BattlePhase = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  
  span {
    font-size: 16px;
    font-weight: normal;
    opacity: 0.8;
    color: ${({ theme }) => theme.isDarkMode ? 'white' : '#000'};
  }
  
  @media (max-width: 768px) {
    span {
      font-size: 14px;
    }
  }
`;

const PausedLabel = styled.span`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  color: ${({ theme }) => theme.isDarkMode ? 'white' : '#000'};
`;

const BattleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--brand, #FF4B3F);
  color: white;
  border: none;
  border-radius: 200px;
  padding: 7px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 238px;
  height: 72px;
  
  &:hover {
    background: #f44d4f;
  }
  
  &:active {
    background: #e64545;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 1440px) {
    width: auto;
    height: auto;
  }
  
  @media (max-width: 768px) {
    width: auto;
    height: auto;
    padding: 10px 24px;
    font-size: 16px;
  }
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
`;

const LoginText = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.text};
  margin-bottom: 24px;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 16px;
`;

const LoginButton = styled.button`
  min-width: 120px;
  height: 44px;
  border-radius: 22px;
  border: none;
  background: #FF4D4F;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0 24px;
  
  &:hover {
    background: #E53E3E;
  }
`;

const RegisterButton = styled.button`
  min-width: 120px;
  height: 44px;
  border-radius: 22px;
  border: 1px solid #FF4D4F;
  background: transparent;
  color: #FF4D4F;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0 24px;
  
  &:hover {
    background: rgba(255, 77, 79, 0.05);
  }
`;

const History = () => {
  const [battles, setBattles] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const pageSize = 10;
  const {
    fightInfo: wsBattleData,
    fightStatus: wsBattleStatus,
    isConnected,
    sendHeartbeat,
    lastSecond: wsCountdown,
    formatCountdown,
    isStop
  } = useWebSocket();
  const [isScrolled, setIsScrolled] = useState(false);
  const { user } = useUser();
  const { showLoginModal, showRegisterModal } = useAuth();
  const isLoggedIn = !!user;
  const scrollRef = useRef(null);

  // 添加调试信息
  useEffect(() => {
    console.log('WebSocket 连接状态:', {
      isConnected,
      fightStatus: wsBattleStatus,
      fightInfo: wsBattleData,
      countdown: wsCountdown  // 添加倒计时数据的日志
    });
  }, [isConnected, wsBattleStatus, wsBattleData, wsCountdown]);  // 添加 wsCountdown 依赖

  const hasBattle = wsBattleStatus && wsBattleStatus !== 0 && wsBattleStatus !== 40;

  // 添加日期格式化函数
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const fetchSequence = useRef(0);
  const fetchBattles = useCallback(async (quiet = false) => {
    const request = ++fetchSequence.current;
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    
    try {
      if (!quiet) setLoading(true);
      const response = await axios.post('//server.battleverse.cn/index/fight/list.do',
        {
          page,
          limit: pageSize
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (request === fetchSequence.current && response.data.success) {
        setBattles(response.data.data.list);
        setTotal(response.data.data.total);
      }
    } catch (error) {
      console.error('获取对战列表失败:', error);
    } finally {
      if (request === fetchSequence.current) setLoading(false);
    }
  }, [page, pageSize, isLoggedIn]);

  useEffect(() => {
    fetchBattles();
  }, [page, fetchBattles]); // 只在页码变化时重新获取数据

  useLiveRefresh(() => fetchBattles(true), isLoggedIn, 15000, 'pb:history-changed');

  // 按日期对对战进行分组
  const groupedBattles = battles.reduce((groups, battle) => {
    const date = formatDate(battle.createTime);
    if (!groups[date]) {
      groups[date] = {
        date: date,
        battles: []
      };
    }
    groups[date].battles.push(battle);
    return groups;
  }, {});

  // 将分组转换为数组并按日期排序
  const sortedGroups = Object.entries(groupedBattles)
    .map(([date, group]) => ({
      ...group,
      date
    }))
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleViewDetail = (fightId) => {
    navigate(`/battle/${fightId}`);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  // 获取当前阶段文本
  const getStageText = () => {
    if (!wsBattleStatus || wsBattleStatus === 0 || wsBattleStatus === 40) {
      return '无对战';
    }
    switch (wsBattleStatus) {
      case 10:
        return '创作阶段';
      case 20:
        return '上传阶段';
      case 30:
        return '投票阶段';
      default:
        return '无对战';
    }
  };

  // 添加滚动监听
  useEffect(() => {
    const handleScroll = () => {
      // 检测滚动位置以固定横幅，将阈值从200px改为20px
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 20);
      
      // 无限滚动加载
      if (scrollRef.current) {
        const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
        if (scrollTop + clientHeight >= scrollHeight - 200 && !loading && hasBattle) {
          fetchBattles();
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loading, hasBattle, fetchBattles]);

  // 处理头像URL的函数
  const processAvatarUrl = (url) => {
    // 如果没有URL或URL为空，直接返回空字符串
    if (!url || url === '') {
      return '';
    }
    
    // 如果是base64数据，直接返回
    if (url && url.startsWith('data:')) {
      return url;
    }
    
    // 如果已经是https://static.battleverse.cn/userpic/格式的URL，直接返回
    if (url && url.includes('https://static.battleverse.cn/userpic/')) {
      return url;
    }
    
    // 如果是其他完整URL，直接返回
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      return url;
    }
    
    // 如果只是数字，如 "1.png"
    if (url && /^\d+(\.\w+)?$/.test(url)) {
      return `https://static.battleverse.cn/userpic/${url}`;
    }
    
    // 对于所有其他情况，添加服务器前缀并清理路径
    return `https://static.battleverse.cn/userpic/${url.replace(/^\/?(avatars|userpic)\//, '')}`;
  };

  return (
    <PageWrapper>
      <GlobalStyle />
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '80px 0 0',
          width: '100%'
        }}>
          <div style={{
            minWidth: '160px',
            padding: '10px 24px',
            borderRadius: '100px',
            background: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            fontWeight: '500',
            fontSize: '14px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(8px)'
          }}>
            <span style={{
              display: 'inline-block',
              width: '14px',
              height: '14px',
              marginRight: '8px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '50%',
              borderTopColor: '#fff',
              animation: 'spin 1s linear infinite',
              verticalAlign: 'middle'
            }}></span>
            加载中...
            <style>{`
              @keyframes spin {
                to {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </div>
        </div>
      ) : !isLoggedIn ? (
        <>
          <LogoWrapper>
            <LogoContent>
              <Logo
                src={isDarkMode ? "/Group 2082894601_white.svg" : "/Group 2082894601.svg"}
                alt="Prompt Battle"
              />
            </LogoContent>
          </LogoWrapper>
          <LoginContainer>
            <LoginText>登录后可查看历史对战记录</LoginText>
            <ButtonsContainer>
              <LoginButton onClick={() => showLoginModal()}>登录</LoginButton>
              <RegisterButton onClick={() => showRegisterModal()}>注册账号</RegisterButton>
            </ButtonsContainer>
          </LoginContainer>
        </>
      ) : (total > 0 || hasBattle) ? (
        <>
          {!(wsBattleStatus > 0 && wsBattleStatus < 40) && (
            <LogoWrapper>
              <LogoContent>
                <Logo
                  src={isDarkMode ? "/Group 2082894601_white.svg" : "/Group 2082894601.svg"}
                  alt="Prompt Battle"
                />
              </LogoContent>
            </LogoWrapper>
          )}

          {wsBattleStatus > 0 && wsBattleStatus < 40 && isLoggedIn && (
            <BattleBanner className="battle-banner">
              <BannerContainer>
                <BannerThemeLabel>主题</BannerThemeLabel>
                <BannerTitle>
                  {wsBattleData?.fightNameCn ||
                   (wsBattleData?.fightName && wsBattleData.fightName.includes('|') ?
                    wsBattleData.fightName.split('|')[0].trim() :
                    wsBattleData?.fightName) ||
                   '当前未设置对战主题'}
                </BannerTitle>
                {(wsBattleData?.fightDesc || wsBattleData?.appendix) && (
                  <BannerDescription>
                    {wsBattleData?.fightDesc && <span className="description-label">描述</span>}
                    <div className="description-content-row">
                      {wsBattleData?.fightDesc && <span>{wsBattleData?.fightDesc}</span>}
                      {wsBattleData?.appendix && (
                        <BannerAppendix
                          href={wsBattleData.appendix.startsWith('http') ? wsBattleData.appendix : `https://static.battleverse.cn${wsBattleData.appendix}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            className="preview-img"
                            src={wsBattleData.appendix.startsWith('http') ? wsBattleData.appendix : `https://static.battleverse.cn${wsBattleData.appendix}`}
                            alt="预览"
                          />
                          下载参考图
                          <div className="download-icon">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M8 2v8M8 10l-3-3M8 10l3-3"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </BannerAppendix>
                      )}
                    </div>
                  </BannerDescription>
                )}
                <BannerStatus>
                  <BannerPhase>
                    <span>{getStageText()}</span>
                    <BannerTimer $isPaused={isStop === 1}>
                      {formatCountdown(wsCountdown)}
                      {isStop === 1 && <PauseText>（已暂停）</PauseText>}
                    </BannerTimer>
                  </BannerPhase>
                  <BannerButton onClick={() => navigate('/battle')}>
                    立即参与
                    <svg width="64" height="64" viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0 10h30M30 10c-2 0-8-4-12-8M30 10c-2 0-8 4-12 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </BannerButton>
                </BannerStatus>
              </BannerContainer>
            </BattleBanner>
          )}
          
          <PageContainer $hasBattle={wsBattleStatus > 0 && wsBattleStatus < 40 && isLoggedIn}>
            {battles.length === 0 ? (
              <EmptyContainer>
                暂无对战记录
              </EmptyContainer>
            ) : (
              <>
                {sortedGroups.map((group, groupIndex) => {
                  return (
                    <BattleSection key={group.date}>
                      <BattleHeader>
                        <BattleDate>{group.date}</BattleDate>
                      </BattleHeader>
                      
                      <BattleGrid>
                        {group.battles.map((battle, battleIndex) => (
                          <BattleCard key={battle.fightId} onClick={() => handleViewDetail(battle.fightId)}>
                            <ImageGroup>
                              {battle.winnerList.length === 1 ? (
                                <div className="main-image-container" style={{ flex: '1' }}>
                                  <img
                                    className="main-image"
                                    loading="lazy" src={historyThumbnail(battle.winnerList[0]?.creationUrl)}
                                    alt={battle.fightName}
                                  />
                                </div>
                              ) : (
                                <>
                                  <div className="main-image-container">
                                    {battle.winnerList.length === 0 ? (
                                      <div className="no-image-text">暂无作品</div>
                                    ) : (
                                      <img
                                        className="main-image"
                                        loading="lazy" src={historyThumbnail(battle.winnerList[0]?.creationUrl)}
                                        alt={battle.fightName}
                                      />
                                    )}
                                  </div>
                                  <div className="sub-images">
                                    {[1, 2].map((index) => {
                                      if (battle.winnerList.length === 2) {
                                        if (index === 1) {
                                          return (
                                            <div
                                              key={index}
                                              className="sub-image-container"
                                              style={{ height: '100%' }}
                                            >
                                              <img
                                                loading="lazy" src={historyThumbnail(battle.winnerList[index]?.creationUrl)}
                                                alt={battle.winnerList[index]?.userName}
                                                style={{ height: '100%', objectFit: 'cover' }}
                                              />
                                            </div>
                                          );
                                        }
                                        return null;
                                      }
                                      
                                      return (
                                        <div key={index} className="sub-image-container">
                                          {battle.winnerList.length <= index ? (
                                            <div className="no-image-text">暂无作品</div>
                                          ) : (
                                            <img
                                              loading="lazy" src={historyThumbnail(battle.winnerList[index]?.creationUrl)}
                                              alt={battle.winnerList[index]?.userName}
                                            />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </ImageGroup>
                            
                            <CardContent>
                              <CardBattleInfo>
                                <BattleTitle>
                                  {battle.fightNameCn || battle.fightName?.split('|')[0].trim()}
                                </BattleTitle>
                                <WinnerLabel>{isArchiveBattle(battle) ? '获奖者' : '获胜者'}</WinnerLabel>
                                <UserInfo>
                                  {battle.winnerList.length > 0 ? (
                                    battle.winnerList.map((winner, index) => (
                                      <UserRow key={index}>
                                        {processAvatarUrl(winner?.userPic) ? (
                                          <UserAvatar src={processAvatarUrl(winner?.userPic)} alt={winner?.userName} />
                                        ) : null}
                                        <UserName>{winner?.userName}</UserName>
                                      </UserRow>
                                    ))
                                  ) : (
                                    <UserRow>
                                      <UserName>暂无获胜者</UserName>
                                    </UserRow>
                                  )}
                                </UserInfo>
                              </CardBattleInfo>
                              <ViewButton>
                                查看
                                <div className="arrow-icon">
                                  <svg width="24" height="20" viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M0 10h30M30 10c-2 0-8-4-12-8M30 10c-2 0-8 4-12 8"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              </ViewButton>
                            </CardContent>
                          </BattleCard>
                        ))}
                      </BattleGrid>
                    </BattleSection>
                  );
                })}
                
                {total > pageSize && (
                  <PaginationContainer>
                    <Pagination
                      current={page}
                      total={total}
                      pageSize={pageSize}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                    />
                  </PaginationContainer>
                )}
              </>
            )}
          </PageContainer>
        </>
      ) : (
        <EmptyContainer>
          暂无对战记录
        </EmptyContainer>
      )}
    </PageWrapper>
  );
};

export default History;

